import { RPWDataReader } from "../../livedataFuncs";
import { dateToXMLDate, isXMLDate } from "../../util/genericDateAndTimeFuncs";
import { ProvincesShortCode, lddGlobalSessionInfo } from "../navigationTypes";
import { ID_FIRMCONTACT, ID_LAWYERONFILE, TaskPreferenceItem, TEXTID_FIRMCONTACT } from "../../../../libs/taskmanagement/src/taskTypes";
import { DashboardKeyDate, DateRange } from "./KeyDateDashboard";
import { TKeyDate } from "./KeyDates";
import { processTaskProfile } from '../../../../libs/taskmanagement/src/TaskModule';
import axios from 'axios';
import { parseProfile, updateStandardItem } from '../Settings/TaskManagementSettings/taskFuncs';
import { convertXMLItemsToJSON } from '../../lib/xmlFuncs';
import Holidays from 'date-holidays';

export type ApplicableDate = {
    id: number,
    title: string,
    date: string,
}

export interface KeyDatesDefineInterface {
    dates: Array<TKeyDate>
}

namespace KeyDate {
    let provinceCode: ProvincesShortCode = undefined;
    export const getProv = () => {
        return provinceCode;
    }
    export const setProv = (prov: ProvincesShortCode) => {
        provinceCode = prov;
    }

    function getIterimDateCaption(): string {
        let ret = "Interim Closing";
        if (provinceCode === "SK") {
            ret = "Interim Possession";
        }
        return ret;
    }

    function getRequisionDateCaption(psm: string): string {
        let ret = "Requisition";
        if (provinceCode === "NS") {
            ret = "Objection";
        } else if (provinceCode === "BC") {
            ret = "Undertaking";
            if (psm === "S") {
                ret = "Undertaking Letter Received";
            }
        }
        return ret;
    }

    function getRequisionReceivedDateCaption(): string {
        let ret = "Requisition Received";
        if (provinceCode === "NS") {
            ret = "Objection Received";
        }
        return ret;
    }

    function getRequisionSentDateCaption(): string {
        let ret = "Requisition Letter Sent";
        if (provinceCode === "NS") {
            ret = "Objection Letter Sent";
        }
        return ret;
    }

    function getEarlyPossessionDateCaption(): string {
        let ret = "Early Possession";
        if (provinceCode === "BC") {
            ret = "Possession";
        }
        return ret;
    }

    function getClosingDateCaption(psm: string): string {
        let ret = "Closing";
        if (provinceCode === "MB") {
            ret = "Possession";
            if (psm === "M") {
                ret = "Closing";
            }
        } else if (provinceCode === "SK") {
            ret = "Possession";
            if (psm === "M") {
                ret = "Advance";
            }
        }
        return ret;
    }

    function getStandardCalendarItem(title: string): string {
        try{
            if (['Possession', 'Advance'].includes(title)) {
                return 'Closing';
            }
            return title;
        } catch (error) {
            return title;
        }
    }
    
    export function getApplicableTasks(
        DataReaderObj: RPWDataReader, 
        myDates: Array<ApplicableDate>, 
        inFileTasks: any[], 
        taskProfiles: TaskPreferenceItem[], 
        allLawyers: any[], 
        provinceTasks: any[], 
        taskFuncs: any,
        taskSettings: Record<string, string>,
        province: string
    ) {
        let updatedInFileTasks = [];
        const firmContact: string[] = DataReaderObj.getFirmContactID()
        const lawyerOnFile = DataReaderObj.getUserID();
        const appID: string = DataReaderObj.getAppId();
        const ownerID: string = DataReaderObj.getLawyerId();
        const psm = DataReaderObj.getFileType();
        const clientName = DataReaderObj.getClientName();
        const clientNameSurnames = DataReaderObj.getClientNameSurnames();
        const matterNumber = DataReaderObj.getFileNumber();
        const lawyerInitials = DataReaderObj.getLawyerInitials();
        const clerkInitials = DataReaderObj.getStaffInitials(); 
        const reLine = DataReaderObj.getReline();      
        inFileTasks = inFileTasks.filter(task => parseInt(task.appId) === parseInt(appID));
        const fileSpecificTasks = inFileTasks.filter(task => parseInt(task.typeID) > 999999);
        const hdObj = new Holidays('CA', province);

        taskProfiles.forEach(taskProfile => {
            const {updatedTask} = processTaskProfile(taskProfile, province, myDates, inFileTasks, provinceTasks, firmContact, lawyerOnFile, appID, ownerID, taskSettings, psm, clientName, clientNameSurnames, matterNumber, lawyerInitials, clerkInitials, reLine, {ID_FIRMCONTACT, ID_LAWYERONFILE}, hdObj);
            if (updatedTask) {
                updatedInFileTasks.push(updatedTask);
            }
        })
        fileSpecificTasks.forEach(task => {
            updatedInFileTasks.push(task);
        })

        return updatedInFileTasks;
    }

    export function getApplicableDates(DataReaderObj: RPWDataReader): Array<ApplicableDate> {
        const dates: Array<ApplicableDate> = [];
        const psm = DataReaderObj.getPSM();
        const myDates = KeyDatesDefine.dates;

        if (KeyDate.getProv() === undefined) {
            return dates;
        }
        for (let i = 0; i < myDates.length; i++) {
            const thisDate: TKeyDate = myDates[i];
            if (thisDate.province === undefined || thisDate.province?.includes(KeyDate.getProv())) {
                if (thisDate.psm === undefined || thisDate.psm?.indexOf(psm) >= 0) {
                    let lddDate = DataReaderObj[thisDate.dateFunc]();
                    if (lddDate > "") {
                        if (!isXMLDate(lddDate)) {// make sure lddDate is in XMLDate format
                            lddDate = dateToXMLDate(new Date(lddDate))
                        }
                        const title = (thisDate.title) ? thisDate.title : (thisDate.titleFuncCheckPSM ? thisDate.titleFunc(psm) : thisDate.titleFunc());
                        const dayMsg: ApplicableDate = { id: thisDate.id, title: title, date: lddDate };
                        dates.push(dayMsg);
                    }
                }
            }
        }
        return dates;
    }

    export const SaveKeyDates = (DataReaderObj: RPWDataReader, SESSION_INFO: lddGlobalSessionInfo, keyDateTasks?: any[]): void => {
        const SaveBody = {
            appId: DataReaderObj.getAppId(),
            matterNumber: DataReaderObj.getFileNumber(),
            PSM: DataReaderObj.getPSM(),
            keydates: getApplicableDates(DataReaderObj),
            tasks: keyDateTasks ?? []
        }
        fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${SESSION_INFO.appProvinceShort}/${SESSION_INFO.firm.id}/save/keydate?SessionID=${SESSION_INFO.sid}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(SaveBody)
        })
            .then(async (response) => {
                if (!response.ok) {
                    const errorText = await response.text().catch(() => 'Unknown error');
                    console.error(`Failed to save key dates: ${response.status} ${response.statusText} - ${errorText}`);
                    throw new Error(`Save key dates failed: ${response.status} ${response.statusText}`);
                }
                return response.json();
            })
            .catch((error) => {
                console.error('Error saving key dates:', error);
                // Error is logged but not thrown to avoid breaking the calling code
                // Consider adding user notification here if needed
            })
    }

    export const LoadInFileTasks = async (AppID: string, SESSION_INFO: lddGlobalSessionInfo): Promise<any[]> => {
        return fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${SESSION_INFO?.appProvinceShort}/${SESSION_INFO?.firm.id}/infilekeydates/?SessionID=${SESSION_INFO?.sid}&AppID=${AppID}`)
            .then(async (response) => {
                if (!response.ok) {
                    const errorText = await response.text().catch(() => 'Unknown error');
                    console.error(`Failed to load in-file tasks: ${response.status} ${response.statusText} - ${errorText}`);
                    return [];
                }
                const data = await response.json();
                return data.result || [];
            })
            .catch((error) => {
                console.error('Error loading in-file tasks:', error);
                return [];
            })
    }

    export const getTaskProfiles = async (SESSION_INFO: lddGlobalSessionInfo): Promise<{
        taskProfiles: any[],
        taskSettings: Record<string, string>
    }> => {
        try {
            let provinceTasks = [];
            const province = SESSION_INFO?.appProvinceShort;
            KeyDate.setProv(province);
            const keydates = KeyDate.KeyDatesDefine.dates;
            for (const keydate of keydates) {
                const isValidProvince = !keydate.province || keydate.province.includes(province);
                if(isValidProvince){
                    if(!keydate.title && keydate.titleFunc){
                        keydate.title = keydate.titleFunc();
                    }
                    provinceTasks.push(keydate);
                }
            }
            const response = await fetch(`getprofile.asp?pname=CalendarProfile&lfdata=1&SessionID=${SESSION_INFO?.sid}`);
            const data = await response.text();
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(data, "text/xml");
            
            let taskSettings: Record<string, string> = {
                ShowTransactionTypeShort: "1",
                ShowClientName: "1",
                ShowTransactionType: "1",
                ShowClerk: "1", 
                ShowFileNumber: "1",
                ShowLawyer: "1"
            };
            
            let taskProfiles: any[] = [];
            
            if (xmlDoc) {
                const items = xmlDoc.getElementsByTagName('items')[0];
                if (!items) {
                    return { taskProfiles: [], taskSettings };
                }
                
                // Extract settings
                if (items.getAttribute('ShowTransactionTypeShort') && items.getAttribute('ShowTransactionTypeShort') > "") {
                    taskSettings.ShowTransactionTypeShort = items.getAttribute('ShowTransactionTypeShort');
                }
                if (items.getAttribute('ShowClientName') && items.getAttribute('ShowClientName') > "") {
                    taskSettings.ShowClientName = items.getAttribute('ShowClientName');
                }
                if (items.getAttribute('ShowTransactionType') && items.getAttribute('ShowTransactionType') > "") {
                    taskSettings.ShowTransactionType = items.getAttribute('ShowTransactionType');
                }
                if (items.getAttribute('ShowClerk') && items.getAttribute('ShowClerk') > "") {
                    taskSettings.ShowClerk = items.getAttribute('ShowClerk');
                }
                if (items.getAttribute('ShowFileNumber') && items.getAttribute('ShowFileNumber') > "") {
                    taskSettings.ShowFileNumber = items.getAttribute('ShowFileNumber');
                }
                if (items.getAttribute('ShowLawyer') && items.getAttribute('ShowLawyer') > "") {
                    taskSettings.ShowLawyer = items.getAttribute('ShowLawyer');
                }
                
                // Extract task profiles
                const standardTaskItems = convertXMLItemsToJSON(items.getElementsByTagName('standardCalendarItems')[0]);
                const userTaskItems = convertXMLItemsToJSON(items.getElementsByTagName('userCalendarItems')[0]);
                let defaultStandardItems = convertXMLItemsToJSON(items.getElementsByTagName('standardCalendarItems')[1]);
                if(!defaultStandardItems || defaultStandardItems.length === 0){
                    defaultStandardItems = convertXMLItemsToJSON(xmlDoc.getElementsByTagName('standardCalendarItems')[1]);
                }
                
                if (defaultStandardItems && defaultStandardItems.length > 0) {
                    standardTaskItems.forEach(standardItem => {
                        const defaultItem = defaultStandardItems.find(item => item.typeID === standardItem.typeID);
                        if (defaultItem) {
                            standardItem = updateStandardItem(standardItem, defaultItem);
                        }
                        if(!standardItem.idsAssignedTo){
                            standardItem.idsAssignedTo = `${ID_FIRMCONTACT}-${ID_FIRMCONTACT}-${TEXTID_FIRMCONTACT}` ;
                        }
                    });
                    
                    defaultStandardItems.forEach(defaultItem => {
                        const exists = standardTaskItems.some(item => item.typeID === defaultItem.typeID);
                        if (!exists) {
                            standardTaskItems.push(defaultItem);
                        }
                    });
                }
                
                let result = [...standardTaskItems, ...userTaskItems];
                result = parseProfile(result);

                for (let i = 0; i < result.length; i++) {
                    const taskProfile = result[i];
                    
                    if (taskProfile.CalendarItemID && taskProfile.CalendarItemID !== "") {
                        continue;
                    }
                    
                    if (taskProfile.CalendarItemDate && taskProfile.CalendarItemDate !== "") {
                        const matchingTask = provinceTasks.find(task => 
                            (task.title ? getStandardCalendarItem(task.title) : getStandardCalendarItem(task.titleFunc())) === taskProfile.CalendarItemDate
                        );
                        
                        if (matchingTask) {
                            result[i] = {
                                ...taskProfile,
                                CalendarItemID: matchingTask.id.toString()
                            };
                        }
                    }
                }
                if (result) {
                    taskProfiles = result;
                }
            }
            
            return { taskProfiles, taskSettings };
        } catch (error) {
            console.error("Error fetching task profiles:", error);
            return { taskProfiles: [], taskSettings: {} };
        }
    };

    export const getUsers = async (userID: string, SESSION_INFO: lddGlobalSessionInfo): Promise<any[]> => {
        try {
            // Fetch users
            const userResponse = await axios.get(`/${process.env.REACT_APP_UVAPI_ENDPOINT}/api/${userID}/uv/get/users?SessionID=${SESSION_INFO?.sid}`);
            const formattedUsers = userResponse.data.result.map((user: any) => ({
                UserID: user.LDDUserID,
                Name: user.Name,
                LDDUserName: user.SubUsername,
                SubUserId: user.SubUserID
            }));
            
            // Then fetch lawyers
            const lawyersResponse = await axios.get(`/${process.env.REACT_APP_UVAPI_ENDPOINT}/api/${userID}/uv/get/lawyers?SessionID=${SESSION_INFO?.sid}`);
            
            if (lawyersResponse.status === 200) {
                const formattedLawyers = lawyersResponse.data.result.lawyersData.map((lawyer: any) => ({
                    UserID: 0,
                    Name: `${lawyer.FirstName} ${lawyer.LastName}`,
                    LDDUserName: "",
                    SubUserId: 0,
                    LSUCID: lawyer.LSUCID
                }));
                
                // Combine both lists
                return [...formattedUsers, ...formattedLawyers];
            } else {
                return formattedUsers;
            }
        } catch (error) {
            console.error("Unable to fetch users and lawyers for adding/editing tasks:", error);
            return [];
        }
    };

    export const MassUpdateTasks = async (SESSION_INFO: lddGlobalSessionInfo, updatedTaskProfile: TaskPreferenceItem, provinceTasks: any[], taskSettings: Record<string, string>): Promise<any[]> => {
        try {
            const SaveBody = {
                updatedTaskProfile: updatedTaskProfile,
                provinceTasks: provinceTasks,
                taskSettings: taskSettings,
                ID_FIRMCONTACT: ID_FIRMCONTACT,
                ID_LAWYERONFILE: ID_LAWYERONFILE
            }
            const response = await fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${SESSION_INFO.appProvinceShort}/${SESSION_INFO.firm.id}/save/massUpdateTasks?typeID=${updatedTaskProfile.typeID}&SessionID=${SESSION_INFO.sid}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(SaveBody)
            })
            
            if (!response.ok) {
                const errorText = await response.text().catch(() => 'Unknown error');
                console.error(`Failed to mass update tasks: ${response.status} ${response.statusText} - ${errorText}`);
                throw new Error(`Mass update tasks failed: ${response.status} ${response.statusText}`);
            }
            
            return await response.json();
        } catch (error) {
            console.error('Error in MassUpdateTasks:', error);
            throw error; // Re-throw to allow caller to handle
        }
    }

    export const LoadKeyDates = async (SESSION_INFO: lddGlobalSessionInfo, range: DateRange): Promise<DashboardKeyDate[]> => {
        return fetch(`/${process.env.REACT_APP_RWAPI_ENDPOINT}/api/${SESSION_INFO.appProvinceShort}/${SESSION_INFO.firm.id}/keydates/${range}?SessionID=${SESSION_INFO.sid}`)
            .then(async (response) => {
                if (!response.ok) {
                    const errorText = await response.text().catch(() => 'Unknown error');
                    console.error(`Failed to load key dates: ${response.status} ${response.statusText} - ${errorText}`);
                    return [];
                }
                const data = await response.json();
                return data.result || [];
            })
            .catch((error) => {
                console.error('Error loading key dates:', error);
                return [];
            })
    }
    export const KeyDatesDefine: KeyDatesDefineInterface = {
        dates: [
            {
                id: 11,
                title: "File Creation",
                psm: "PSM",
                dateFunc: "getFileCreationDate"
            },
            {
                id: 12,
                title: "Contract",
                psm: "PSM",
                dateFunc: "getContractDate"
            },
            {
                id: 13,
                title: "Lawyer Review Date",
                psm: "PSM",
                province: ["NS"],
                dateFunc: "getLawyerReviewDate"
            },
            {
                id: 3,
                title: "Conditional Until",
                psm: "PS",
                dateFunc: "getCoditionalDate"
            },
            {
                id: 4,
                title: "Adjustment",
                psm: "PS",
                dateFunc: "getAdjustmentDate"
            },
            {
                id: 5,
                titleFunc: getEarlyPossessionDateCaption,
                psm: "PS",
                province: ["AB", "BC"],
                dateFunc: "getEarlyPossessionDate"
            },
            {
                id: 6,
                titleFunc: getRequisionDateCaption,
                titleFuncCheckPSM: true,
                psm: "PS",
                province: ["ON", "NS", "BC"],
                dateFunc: "getRequisionDate"
            },
            {
                id: 7,
                titleFunc: getRequisionSentDateCaption,
                psm: "P",
                province: ["ON", "NS"],
                dateFunc: "getRequisitionSentDate"
            },
            {
                id: 8,
                titleFunc: getRequisionReceivedDateCaption,
                psm: "S",
                province: ["ON", "NS"],
                dateFunc: "getRequisitionReceivedDate"
            },
            {
                id: 9,
                title: "Trust Letter",
                psm: "PS",
                province: ["MB", "SK"],
                dateFunc: "getReverseTrustLetterDate"
            },
            {
                id: 10,
                title: "Undertaking Response Letter",
                psm: "PS",
                province: ["BC"],
                dateFunc: "getRequisitionResponseDate"
            },
            {
                id: 2,
                titleFunc: getIterimDateCaption,
                psm: "PS",
                province: ["SK", "ON", "NS", "MB", "AB"],
                dateFunc: "getInterimClosingDate"
            },
            {
                id: 1,
                titleFunc: getClosingDateCaption,
                titleFuncCheckPSM: true,
                psm: "PSM",
                dateFunc: "getClosingDate"
            }
        ]
    }
}
export default KeyDate;